import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useSearchParam } from "@/lib/use-search-param";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  CheckCircle2,
  ChevronRight,
  Eye,
  FileDown,
  Mail,
  MoreHorizontal,
  Play,
  Plus,
  RefreshCw,
  Search,
  TrendingUp,
  UserX,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { apiPost, queryClient } from "@/lib/api";
import { apiGetAllPages } from "@/lib/paging";
import {
  ExportDialog,
  type ExportTarget,
} from "@/components/shared/export-dialog";
import { useAccountant } from "@/lib/auth";
import { entityTypeLabel, isAustralia, offersCommission } from "@/lib/country";
import { CommissionStepper } from "@/components/shared/commission-stepper";
import {
  REMINDER_TEMPLATE_LABELS,
  ReminderDialog,
  type ReminderTemplate,
} from "@/components/shared/reminder-dialog";
import { BulkActionBar } from "@/components/shared/bulk-action-bar";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { SortableHeader } from "@/components/shared/sortable-header";
import { ReinviteDialog } from "@/components/shared/reinvite-dialog";
import { useBulkSelection } from "@/hooks/use-bulk-selection";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CommissionBadge,
  ConnectionBadge,
  type ConnectionStatus,
  type SubscriptionStatus,
  type CommissionStatus,
  SubscriptionBadge,
} from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryError } from "@/components/shared/query-error";
import { PendingRequests } from "@/pages/accountant/pending-requests";
import { cn } from "@/lib/utils";

interface AccountantUserRow {
  id: string;
  invite_email: string;
  display_name: string;
  entity_type: string;
  status: ConnectionStatus;
  subscription_status: SubscriptionStatus;
  commission_status: CommissionStatus;
  receipts_count: number;
  subscription_plan_type?: string;
  subscription_started_at?: string | null;
  subscription_ended_at?: string | null;
  subscription_expires_at?: string | null;
  /** The client added this practice from the app. Never earns commission. */
  client_initiated?: boolean;
}

interface ClientListResponse {
  results: AccountantUserRow[];
  total_count: number;
  has_more: boolean;
}

// The clients endpoint serves at most 200 rows per request.
const CLIENT_LIST_PAGING = { pageSize: 200, maxRows: 2000 };

// Tab filter definitions (replaces left sidebar)
const TAB_FILTERS = [
  { key: "", label: "All" },
  { key: "invited", label: "Invited" },
  { key: "accepted", label: "Accepted" },
  { key: "subscribed", label: "Subscribed" },
  { key: "disconnected", label: "Disconnected" },
  { key: "declined", label: "Declined" },
] as const;

export function Dashboard() {
  const navigate = useNavigate();
  const { data: accountant } = useAccountant();
  // The practice's own country: decides the AU-only pieces of this page (the
  // BAS reminder and the referral commission programme).
  const isAU = isAustralia(accountant?.country_config);
  const showCommission = offersCommission(accountant?.country_config);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useSearchParam("filter", "");
  type ClientSortField =
    | "display_name"
    | "subscription_status"
    | "commission_status"
    | "receipts_count"
    | "status";
  const [sortField, setSortField] = useState<ClientSortField>("display_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const toggleSort = (f: ClientSortField) => {
    if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(f);
      setSortDir("asc");
    }
  };
  const [stepperUser, setStepperUser] = useState<AccountantUserRow | null>(
    null,
  );
  // Welcome is derived: open when accountant loaded and never dismissed.
  // `welcomeDismissed` flips true on explicit dismiss so the modal closes
  // without re-reading localStorage mid-render.
  const [welcomeDismissed, setWelcomeDismissed] = useState(
    () => typeof window !== "undefined" && !!localStorage.getItem("taxr_welcomed"),
  );
  // Welcome video plays first by default. Accountants who haven't dismissed
  // the welcome modal land on the autoplaying walkthrough; "Back to overview"
  // flips this to surface the onboarding checklist underneath.
  const [playingWelcomeVideo, setPlayingWelcomeVideo] = useState(true);
  const [reminderTarget, setReminderTarget] = useState<
    | { kind: "single"; id: string; name: string }
    | { kind: "bulk"; ids: string[] }
    | null
  >(null);
  const [confirmState, setConfirmState] = useState<
    | { kind: "disconnect"; id: string; name: string }
    | { kind: "reinvite"; id: string; name: string }
    | null
  >(null);

  const showWelcome = !!accountant && !welcomeDismissed;

  const dismissWelcome = () => {
    localStorage.setItem("taxr_welcomed", "1");
    setWelcomeDismissed(true);
    setPlayingWelcomeVideo(true);
  };

  const buildListQuery = (
    searchValue: string,
    filterValue: string,
    limit: number,
    offset: number,
  ): string => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (searchValue) params.set("search", searchValue);
    // "subscribed" is a subscription filter, not a connection status
    if (filterValue === "subscribed") {
      params.set("status", "accepted");
      params.set("subscription_status", "subscribed");
    } else if (filterValue) {
      params.set("status", filterValue);
    }
    return params.toString();
  };

  const {
    data: clientData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["accountant-users", search, activeFilter],
    // Every page: a practice with more clients than one page holds used to
    // lose its oldest clients from this list.
    queryFn: () =>
      apiGetAllPages<AccountantUserRow, ClientListResponse>(
        (limit, offset) =>
          `/api/accountant/users?${buildListQuery(search, activeFilter, limit, offset)}`,
        CLIENT_LIST_PAGING,
      ),
    // Keep the previous page rendered while the next search query is in
    // flight. Without this the search input briefly unmounts with a Skeleton
    // on every keystroke and loses focus.
    placeholderData: keepPreviousData,
  });

  const { data: allClientData, isLoading: isCountsLoading } = useQuery({
    queryKey: ["accountant-users", "all"],
    queryFn: () =>
      apiGetAllPages<AccountantUserRow, ClientListResponse>(
        (limit, offset) => `/api/accountant/users?limit=${limit}&offset=${offset}`,
        CLIENT_LIST_PAGING,
      ),
  });

  const users = clientData?.results ?? [];

  const sortedUsers = useMemo(() => {
    const out = users.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      let va: string | number = "";
      let vb: string | number = "";
      if (sortField === "display_name") {
        va = (a.display_name || a.invite_email || "").toLowerCase();
        vb = (b.display_name || b.invite_email || "").toLowerCase();
      } else if (sortField === "receipts_count") {
        va = a.receipts_count ?? 0;
        vb = b.receipts_count ?? 0;
      } else if (sortField === "subscription_status") {
        va = a.subscription_status || "";
        vb = b.subscription_status || "";
      } else if (sortField === "commission_status") {
        va = a.commission_status || "";
        vb = b.commission_status || "";
      } else {
        va = a.status || "";
        vb = b.status || "";
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return out;
  }, [users, sortField, sortDir]);

  const counts = useMemo(() => {
    const all = allClientData?.results ?? [];
    return {
      total: allClientData?.total_count ?? 0,
      accepted: all.filter((u) => u.status === "accepted").length,
      subscribed: all.filter(
        (u) =>
          u.status === "accepted" && u.subscription_status === "subscribed",
      ).length,
      invited: all.filter((u) => u.status === "invited").length,
      declined: all.filter((u) => u.status === "declined").length,
      disconnected: all.filter((u) => u.status === "disconnected").length,
    };
  }, [allClientData]);

  const {
    has,
    toggleOne,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
    count: selectedCount,
    ids: selectedIdArray,
  } = useBulkSelection({ items: users, getId: (u) => u.id });

  // ─── Mutations ───

  const reinviteMutation = useMutation({
    mutationFn: ({
      id,
      personal_note,
      subject,
    }: {
      id: string;
      personal_note: string;
      subject?: string;
    }) =>
      apiPost(`/api/accountant/users/${id}/reinvite`, {
        personal_note,
        subject,
      }),
    onSuccess: (_data, variables) => {
      toast.success("Re-invite sent");
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
      queryClient.invalidateQueries({
        queryKey: ["accountant-messages", variables.id],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not re-invite"),
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost(`/api/accountant/users/${id}/disconnect`),
    onSuccess: () => {
      toast.success("Client disconnected");
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
      clearSelection();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not disconnect"),
  });

  const bulkRemindMutation = useMutation({
    mutationFn: (template: ReminderTemplate) =>
      apiPost("/api/accountant/users/bulk-remind", {
        client_ids: selectedIdArray,
        template,
      }),
    onSuccess: (_data, template) => {
      toast.success(
        `Sent "${REMINDER_TEMPLATE_LABELS[template]}" to ${selectedCount} clients`,
      );
      clearSelection();
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "Could not send reminders",
      ),
  });

  const [exportTarget, setExportTarget] = useState<ExportTarget | null>(null);

  const openBulkExport = () => {
    const clients = users
      .filter((u) => has(u.id))
      .map((u) => ({
        id: u.id,
        name: u.display_name || u.invite_email,
        email: u.invite_email,
      }));
    if (!clients.length) return;
    if (clients.length === 1) {
      setExportTarget({
        kind: "single",
        clientId: clients[0].id,
        clientName: clients[0].name,
        clientEmail: clients[0].email,
      });
    } else {
      setExportTarget({ kind: "bulk", clients });
    }
  };

  // ─── Tab count helper ───
  const getTabCount = (key: string): number => {
    if (key === "") return counts.total;
    if (key === "subscribed") return counts.subscribed;
    return counts[key as keyof typeof counts] ?? 0;
  };

  // ─── Render ───

  return (
    <div className="space-y-6">
      {/* Welcome modal */}
      <Dialog open={showWelcome} onOpenChange={(o) => !o && dismissWelcome()}>
        <DialogContent
          className={cn(
            "p-0 gap-0 overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)] transition-[max-width] duration-300",
            playingWelcomeVideo
              ? "max-w-[min(56rem,calc(100vw-2rem))]"
              : "max-w-[min(36rem,calc(100vw-2rem))]",
          )}
        >
          {/* Hidden title when playing so the dialog stays labelled for a11y */}
          {playingWelcomeVideo && (
            <DialogTitle className="sr-only">Taxr welcome video</DialogTitle>
          )}

          {playingWelcomeVideo ? (
            <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-foreground">
              {/* autoplays with sound; if the browser blocks unmuted */}
              {/* autoplay (Safari, low Chrome MEI) the poster stays */}
              {/* visible and the accountant taps the controls to start. */}
              {/* mp4 listed first because iOS Safari has historically */}
              {/* mis-handled webm — same source order as the marketing */}
              {/* site's demo-video partial. */}
              <video
                poster="/videos/taxr-accountant-walkthrough-poster.jpg"
                autoPlay
                controls
                playsInline
                preload="auto"
                className="absolute inset-0 h-full w-full object-contain"
              >
                <source
                  src="/videos/taxr-accountant-walkthrough.mp4"
                  type="video/mp4"
                />
                <source
                  src="/videos/taxr-accountant-walkthrough.webm"
                  type="video/webm"
                />
              </video>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {/* Replay strip — clicking flips back into the autoplaying */}
              {/* video. Uses the local poster so it matches whatever still */}
              {/* the player jumped to mid-encode. */}
              <button
                type="button"
                onClick={() => setPlayingWelcomeVideo(true)}
                className="group relative block aspect-[16/6] w-full shrink-0 overflow-hidden bg-foreground text-left cursor-pointer"
              >
                <img
                  src="/videos/taxr-accountant-walkthrough-poster.jpg"
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-black/45 transition-colors duration-300 group-hover:bg-black/35" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(255,255,255,0.10),transparent_65%)]" />
                <div className="absolute inset-x-0 top-0 h-px bg-white/5" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
                  <div className="flex size-12 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25 backdrop-blur-sm transition-all duration-300 group-hover:scale-105 group-hover:bg-white/25">
                    <Play
                      className="size-[18px] translate-x-px fill-white text-white"
                      aria-hidden
                    />
                  </div>
                  <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/80 [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
                    2-minute walkthrough
                  </span>
                </div>
              </button>

              <div className="px-6 pt-6 pb-2 sm:px-10 sm:pt-8">
                <DialogTitle className="font-heading text-[22px] leading-[1.15] font-medium tracking-tight text-foreground sm:text-[26px] sm:leading-[1.1]">
                  Your portal is ready
                  {accountant?.business_name ? "," : "."}
                </DialogTitle>
                {accountant?.business_name && (
                  <p className="mt-1 font-heading text-[22px] leading-[1.15] font-medium tracking-tight text-muted-foreground sm:text-[26px] sm:leading-[1.1]">
                    {accountant.business_name}.
                  </p>
                )}

                <DialogDescription className="mt-5 max-w-[46ch] text-[14.5px] leading-relaxed text-muted-foreground">
                  Invite clients, review their receipts, send reminders, and
                  export reports, all in one place.
                </DialogDescription>

                <ol className="mt-7 border-t border-border">
                  {[
                    { n: "01", label: "Account created", done: true },
                    {
                      n: "02",
                      label:
                        "Tune your expense categories (drives the AI classifier)",
                      done: (accountant?.categories?.length ?? 0) > 0,
                    },
                    {
                      n: "03",
                      label: "Invite your first clients",
                      done: counts.total > 0,
                    },
                    {
                      n: "04",
                      label: "Review your first receipts",
                      done: counts.subscribed > 0,
                    },
                  ].map((step) => (
                    <li
                      key={step.n}
                      className="flex items-center gap-4 border-b border-border py-3 sm:gap-6"
                    >
                      <span className="text-[11px] font-medium tabular-nums tracking-wider text-muted-foreground/60">
                        {step.n}
                      </span>
                      <span
                        className={cn(
                          "flex-1 text-[14.5px]",
                          step.done
                            ? "text-muted-foreground line-through decoration-muted-foreground/40"
                            : "text-foreground",
                        )}
                      >
                        {step.label}
                      </span>
                      {step.done && (
                        <CheckCircle2
                          className="size-4 text-success"
                          aria-hidden
                        />
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          <div className="mt-auto flex shrink-0 items-center justify-between gap-4 border-t border-border bg-muted/40 px-6 py-4 sm:gap-6 sm:px-10 sm:py-5">
            {playingWelcomeVideo ? (
              <button
                type="button"
                onClick={() => setPlayingWelcomeVideo(false)}
                className="inline-flex items-center gap-1 rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-muted"
              >
                <ArrowLeft className="size-4" aria-hidden />
                Back to overview
              </button>
            ) : (
              <DialogClose
                onClick={dismissWelcome}
                className="rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-muted"
              >
                Skip for now
              </DialogClose>
            )}
            <Button
              autoFocus
              onClick={() => {
                dismissWelcome();
                navigate("/accountant/invite");
              }}
              className="h-10 gap-1.5 px-5"
            >
              Invite clients
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Commission summary cards — D1a: only one commission card (lifetime).
          The commission card is AU-only, like the programme itself. */}
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          showCommission ? "sm:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        {showCommission && (
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-success-muted text-success">
              <Award className="size-5" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Referral commissions
              </p>
              <p className="text-xl font-semibold">
                {accountant?.stats?.commissions_earned ?? 0}
                <span className="text-sm text-muted-foreground font-normal">
                  {" "}
                  earned
                </span>
                {(accountant?.stats?.commissions_earning ?? 0) > 0 && (
                  <span className="text-sm text-muted-foreground font-normal">
                    {" · "}
                    {accountant?.stats?.commissions_earning} in progress
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
        )}
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-info-muted text-info">
              <Users className="size-5" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Active subscribers
              </p>
              <p className="text-xl font-semibold">
                {accountant?.stats?.active_subscribers ?? 0}
                <span className="text-sm text-muted-foreground font-normal">
                  {" "}
                  / {accountant?.stats?.total_users ?? 0} connected
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-foreground/70">
              <TrendingUp className="size-5" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total clients</p>
              <div className="text-xl font-semibold">
                {isCountsLoading ? (
                  <Skeleton className="inline-block h-6 w-8 align-middle" />
                ) : (
                  counts.total
                )}
                <span className="text-sm text-muted-foreground font-normal">
                  {" "}
                  (
                  {isCountsLoading ? (
                    <Skeleton className="inline-block h-3.5 w-12 align-middle" />
                  ) : (
                    <>{counts.invited} pending</>
                  )}
                  )
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients who invited this practice from the app and are waiting to be
          accepted. Absent when there are none. */}
      <PendingRequests />

      {/* Main layout — D1d: no left sidebar, full-width table with tabs */}
      <div className="space-y-4">
        {/* Top bar: title + invite + search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Clients </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 size-4 text-muted-foreground" />
              <Input
                aria-label="Search clients"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) =>
                  setSearch((e.target as HTMLInputElement).value)
                }
                className="pl-9 pr-8 w-64"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              )}
            </div>
            {(search || activeFilter) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearch("");
                  setActiveFilter("");
                }}
                className="cursor-pointer"
              >
                Clear filters
              </Button>
            )}
            <Button
              onClick={() => navigate("/accountant/invite")}
              className="cursor-pointer"
            >
              <Plus className="mr-1.5 size-4" />
              Invite clients
            </Button>
          </div>
        </div>

        {/* Segmented tabs — D1d */}
        <Tabs value={activeFilter} onValueChange={setActiveFilter}>
          <TabsList className="h-9">
            {TAB_FILTERS.map(({ key, label }) => (
              <TabsTrigger
                key={key}
                value={key}
                className="cursor-pointer gap-1.5 text-xs"
              >
                {label}
                {isCountsLoading ? (
                  <Skeleton className="h-3.5 w-5 inline-block" />
                ) : (
                  <span className="tabular-nums text-muted-foreground/70">
                    {getTabCount(key)}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {someSelected && (
          <BulkActionBar count={selectedCount} onDeselectAll={clearSelection}>
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer">
                <Mail className="size-3" />
                Send reminder
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {(Object.keys(REMINDER_TEMPLATE_LABELS) as ReminderTemplate[])
                  .filter((t) => t !== "custom")
                  // BAS is an Australian lodgement: only AU practices get it.
                  .filter((t) => t !== "bas_reminder" || isAU)
                  .map((tpl) => (
                    <DropdownMenuItem
                      key={tpl}
                      onClick={() => bulkRemindMutation.mutate(tpl)}
                    >
                      {REMINDER_TEMPLATE_LABELS[tpl]}
                    </DropdownMenuItem>
                  ))}
                <DropdownMenuItem
                  onClick={() =>
                    setReminderTarget({
                      kind: "bulk",
                      ids: selectedIdArray,
                    })
                  }
                >
                  Custom message...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="xs"
              onClick={openBulkExport}
              className="cursor-pointer"
            >
              <FileDown className="mr-1 size-3" />
              Export selected
            </Button>
          </BulkActionBar>
        )}

        {/* Table or empty state */}
        {isLoading ? (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Skeleton className="size-4 rounded" />
                  </TableHead>
                  <TableHead className="w-[260px]">Name</TableHead>
                  <TableHead>Connection</TableHead>
                  <TableHead>Subscription</TableHead>
                  {showCommission && <TableHead>Commission</TableHead>}
                  <TableHead>Receipts</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="size-4 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-3 w-24 mt-1" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </TableCell>
                    {showCommission && (
                      <TableCell>
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </TableCell>
                    )}
                    <TableCell>
                      <Skeleton className="h-4 w-14" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="size-4" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : isError ? (
          <QueryError
            error={error}
            message={
              error instanceof Error ? error.message : "Could not load clients"
            }
            onRetry={() =>
              queryClient.invalidateQueries({
                queryKey: ["accountant-users"],
              })
            }
          />
        ) : users.length === 0 ? (
          <div className="py-16 text-center">
            {search || activeFilter ? (
              <Users className="mx-auto size-12 text-muted-foreground/50 mb-4" />
            ) : (
              <img
                src="/illustrations/robot-empty.png"
                alt=""
                aria-hidden="true"
                className="mx-auto mb-4 h-36 w-auto opacity-80"
              />
            )}
            <h3 className="text-lg font-semibold">
              {search || activeFilter
                ? "No clients match this filter"
                : "No clients yet"}
            </h3>
            <p className="text-muted-foreground mt-1 mb-6 max-w-sm mx-auto">
              {search || activeFilter
                ? "Try clearing the search or switching filters."
                : "Invite your clients and they'll start uploading receipts straight from their phone."}
            </p>
            {!(search || activeFilter) && (
              <Button
                onClick={() => navigate("/accountant/invite")}
                className="cursor-pointer"
              >
                <Plus className="mr-1.5 size-4" />
                Invite your first clients
              </Button>
            )}
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all clients"
                    />
                  </TableHead>
                  <SortableHeader
                    field="display_name"
                    label="Name"
                    sortField={sortField}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    className="w-[260px]"
                  />
                  <SortableHeader
                    field="status"
                    label="Connection"
                    sortField={sortField}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    field="subscription_status"
                    label="Subscription"
                    sortField={sortField}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                  {showCommission && (
                    <SortableHeader
                      field="commission_status"
                      label="Commission"
                      sortField={sortField}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                  )}
                  <SortableHeader
                    field="receipts_count"
                    label="Receipts"
                    sortField={sortField}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  />
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/accountant/clients/${user.id}`)}
                    data-testid={`client-row-${user.invite_email}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={has(user.id)}
                        onCheckedChange={() => toggleOne(user.id)}
                        aria-label={`Select ${user.display_name || user.invite_email}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {user.display_name || user.invite_email}
                        </p>
                        {user.display_name && (
                          <p className="text-xs text-muted-foreground">
                            {user.invite_email}
                          </p>
                        )}
                        {user.entity_type && (
                          <p className="text-xs text-muted-foreground capitalize">
                            {entityTypeLabel(accountant?.country_config, user.entity_type)}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ConnectionBadge status={user.status} />
                    </TableCell>
                    <TableCell>
                      {user.status === "accepted" ? (
                        <SubscriptionBadge
                          status={user.subscription_status}
                          expiresAt={user.subscription_expires_at}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* F4: Earnings cell is a visually distinct button so
                          accountants can tell it from the row-level click
                          target. Styled as a pill with a hover background,
                          focus ring, and right chevron. The status tooltip
                          is provided by CommissionBadge itself. */}
                    {showCommission && (
                    <TableCell>
                      <button
                        type="button"
                        aria-label={`Open commission breakdown for ${user.display_name || user.invite_email}`}
                        className="group/earnings inline-flex items-center gap-1.5 rounded-full border border-transparent bg-muted/40 py-1 pr-1.5 pl-2 cursor-pointer transition-all hover:border-border hover:bg-muted hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStepperUser(user);
                        }}
                      >
                        <CommissionBadge
                          status={user.commission_status}
                          connectionStatus={user.status}
                          subscriptionStatus={user.subscription_status}
                          clientInitiated={user.client_initiated}
                          noTooltip
                        />
                        <ChevronRight className="size-3.5 text-muted-foreground/70 shrink-0 transition-transform group-hover/earnings:translate-x-0.5 group-hover/earnings:text-foreground" />
                      </button>
                    </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground">
                      {user.receipts_count}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          aria-label={`Actions for ${user.display_name || user.invite_email}`}
                          className="inline-flex size-8 items-center justify-center rounded-md hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="size-4" aria-hidden />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="min-w-[180px]"
                        >
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/accountant/clients/${user.id}`);
                            }}
                          >
                            <Eye className="mr-2 size-4" />
                            View details
                          </DropdownMenuItem>
                          {user.status === "accepted" && (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReminderTarget({
                                  kind: "single",
                                  id: user.id,
                                  name: user.display_name || user.invite_email,
                                });
                              }}
                            >
                              <Mail className="mr-2 size-4" />
                              Send reminder
                            </DropdownMenuItem>
                          )}
                          {(user.status === "declined" ||
                            user.status === "disconnected") && (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmState({
                                  kind: "reinvite",
                                  id: user.id,
                                  name: user.display_name || user.invite_email,
                                });
                              }}
                            >
                              <RefreshCw className="mr-2 size-4" />
                              Re-invite
                            </DropdownMenuItem>
                          )}
                          {user.status === "invited" && (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Route through the reinvite dialog so the
                                // accountant can add a personal note.
                                setConfirmState({
                                  kind: "reinvite",
                                  id: user.id,
                                  name: user.display_name || user.invite_email,
                                });
                              }}
                            >
                              <Mail className="mr-2 size-4" />
                              Resend invite
                            </DropdownMenuItem>
                          )}
                          {user.status !== "disconnected" && (
                            <DropdownMenuItem
                              className="text-destructive cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmState({
                                  kind: "disconnect",
                                  id: user.id,
                                  name: user.display_name || user.invite_email,
                                });
                              }}
                            >
                              <UserX className="mr-2 size-4" />
                              Disconnect
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Commission stepper */}
      {stepperUser && (
        <CommissionStepper
          displayName={stepperUser.display_name}
          inviteEmail={stepperUser.invite_email}
          status={stepperUser.status}
          subscriptionStatus={stepperUser.subscription_status}
          commissionStatus={stepperUser.commission_status}
          subscriptionPlanType={stepperUser.subscription_plan_type}
          subscriptionStartedAt={stepperUser.subscription_started_at}
          subscriptionEndedAt={stepperUser.subscription_ended_at}
          subscriptionExpiresAt={stepperUser.subscription_expires_at}
          clientInitiated={stepperUser.client_initiated}
          open={!!stepperUser}
          onOpenChange={(open) => {
            if (!open) setStepperUser(null);
          }}
        />
      )}

      {/* Reminder dialog */}
      {reminderTarget && (
        <ReminderDialog
          open
          onOpenChange={(o) => !o && setReminderTarget(null)}
          target={reminderTarget}
          // The client list carries no per-client country, so the practice's
          // own country decides which templates are offered here.
          countryConfig={accountant?.country_config}
          onSent={() => {
            clearSelection();
            queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
          }}
        />
      )}

      {/* Export dialog */}
      {exportTarget && (
        <ExportDialog
          open
          onOpenChange={(o) => {
            if (!o) {
              setExportTarget(null);
              clearSelection();
            }
          }}
          target={exportTarget}
          countryConfig={accountant?.country_config}
        />
      )}

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmState?.kind === "disconnect"}
        onOpenChange={(o) => !o && setConfirmState(null)}
        title={`Disconnect ${
          confirmState?.kind === "disconnect" ? confirmState.name : ""
        }?`}
        description="They'll keep their receipts but won't appear in your client list. You can re-invite them anytime."
        confirmLabel="Disconnect"
        destructive
        loading={disconnectMutation.isPending}
        onConfirm={() => {
          if (confirmState?.kind === "disconnect") {
            disconnectMutation.mutate(confirmState.id, {
              onSuccess: () => setConfirmState(null),
            });
          }
        }}
      />
      <ReinviteDialog
        open={confirmState?.kind === "reinvite"}
        onOpenChange={(o) => !o && setConfirmState(null)}
        clientId={confirmState?.kind === "reinvite" ? confirmState.id : ""}
        clientName={confirmState?.kind === "reinvite" ? confirmState.name : ""}
        accountantName={
          accountant?.business_name || accountant?.email || undefined
        }
        loading={reinviteMutation.isPending}
        onConfirm={(note, subject) => {
          if (confirmState?.kind === "reinvite") {
            reinviteMutation.mutate(
              { id: confirmState.id, personal_note: note, subject },
              { onSuccess: () => setConfirmState(null) },
            );
          }
        }}
      />
    </div>
  );
}
